import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLangPrefix } from '../hooks/useLocalePath';
import api from '../services/api';
import '../styles/OnboardingTour.css';

const STEP_ICONS = ['👋', '📁', '📊', '✅', '🔍', '📋', '💳', '🚀'];

export default function OnboardingTour({ onComplete }) {
  const { t } = useTranslation();
  const prefix = useLangPrefix();
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});
  const [arrowDirection, setArrowDirection] = useState('left');
  const tooltipRef = useRef(null);

  // All steps stay on the current page — no navigation.
  // The tour highlights sidebar links (always visible) or shows centered tooltips.
  const TOUR_STEP_TARGETS = [
    { id: 'welcome',         target: null,                                  position: undefined },
    { id: 'projects',        target: `[href="${prefix}/projects"]`,          position: 'right' },
    { id: 'dashboard',       target: `[href="${prefix}/dashboard"]`,         position: 'right' },
    { id: 'assignments',     target: `[href="${prefix}/assignments"]`,       position: 'right' },
    { id: 'stack-detection', target: null,                                  position: undefined },
    { id: 'qa-checklists',   target: null,                                  position: undefined },
    { id: 'billing',         target: `[href="${prefix}/billing"]`,           position: 'right' },
    { id: 'done',            target: null,                                  position: undefined },
  ];

  // Build translated steps from i18n keys
  const tourSteps = t('onboarding.steps', { returnObjects: true }).map((s, i) => ({
    ...s,
    icon: STEP_ICONS[i],
    target: TOUR_STEP_TARGETS[i]?.target ?? null,
    position: TOUR_STEP_TARGETS[i]?.position,
  }));

  const step = tourSteps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === tourSteps.length - 1;
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  const positionTooltip = useCallback(() => {
    if (!step.target) {
      // Center on screen
      setTooltipStyle({ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      setArrowDirection('none');
      return;
    }
    const el = document.querySelector(step.target);
    if (!el) {
      setTooltipStyle({ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      setArrowDirection('none');
      return;
    }
    const rect = el.getBoundingClientRect();
    const pos = step.position || 'right';
    if (pos === 'right') {
      setTooltipStyle({
        position: 'fixed',
        top: `${rect.top + rect.height / 2}px`,
        left: `${rect.right + 16}px`,
        transform: 'translateY(-50%)',
      });
      setArrowDirection('left');
      setArrowStyle({ top: '50%', left: '-8px', transform: 'translateY(-50%) rotate(45deg)' });
    } else if (pos === 'bottom') {
      setTooltipStyle({
        position: 'fixed',
        top: `${rect.bottom + 16}px`,
        left: `${rect.left + rect.width / 2}px`,
        transform: 'translateX(-50%)',
      });
      setArrowDirection('top');
      setArrowStyle({ top: '-8px', left: '50%', transform: 'translateX(-50%) rotate(45deg)' });
    }
    // Highlight the target element
    el.classList.add('tour-highlight');
    return () => el.classList.remove('tour-highlight');
  }, [step]);

  useEffect(() => {
    const cleanup = positionTooltip();
    // Reposition on resize
    window.addEventListener('resize', positionTooltip);
    return () => {
      window.removeEventListener('resize', positionTooltip);
      if (cleanup) cleanup();
      // Cleanup highlight from all elements
      document.querySelectorAll('.tour-highlight').forEach(el => el.classList.remove('tour-highlight'));
    };
  }, [positionTooltip, currentStep]);

  const handleNext = () => {
    if (isLast) {
      handleComplete();
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    if (isFirst) return;
    setCurrentStep(prev => prev - 1);
  };

  const handleComplete = async () => {
    try {
      await api.patch('/auth/tour-complete');
    } catch {
      // Non-blocking
    }
    onComplete();
  };

  const handleSkip = () => {
    handleComplete();
  };

  return (
    <>
      {/* Overlay */}
      <div className="tour-overlay" onClick={handleSkip} />
      {/* Tooltip */}
      <div className="tour-tooltip" style={tooltipStyle} ref={tooltipRef}>
        {arrowDirection !== 'none' && (
          <div className={`tour-arrow tour-arrow-${arrowDirection}`} style={arrowStyle} />
        )}
        {/* Progress bar */}
        <div className="tour-progress-bar">
          <div className="tour-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="tour-content">
          <div className="tour-step-icon">{step.icon}</div>
          <h3 className="tour-step-title">{step.title}</h3>
          <p className="tour-step-description">{step.description}</p>
        </div>
        <div className="tour-footer">
          <div className="tour-step-indicator">
            {currentStep + 1} / {tourSteps.length}
          </div>
          <div className="tour-actions">
            {!isFirst && (
              <button className="tour-btn tour-btn-secondary" onClick={handlePrev}>
                {t('onboarding.previous')}
              </button>
            )}
            {!isLast ? (
              <>
                <button className="tour-btn tour-btn-ghost" onClick={handleSkip}>
                  {t('onboarding.skip')}
                </button>
                <button className="tour-btn tour-btn-primary" onClick={handleNext}>
                  {t('onboarding.next')}
                </button>
              </>
            ) : (
              <button className="tour-btn tour-btn-primary" onClick={handleComplete}>
                {t('onboarding.finish')}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
